import { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button/Button';
import { theme } from '../../theme/theme';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useCloseOngoingTrip from '../../API/hooks/trips/useCloseOngoingTrip';
import { TripDetails } from '../../API/types/Trips';
import { getFormattedDateAndTime, getTimeZone, getTripDuration } from '../../utils/dateUtils';
import 'react-datepicker/dist/react-datepicker.css';
import S from './CloseTripFormStyles';

/**
 * A trip that has been ongoing long enough to be closed manually, together with the backend's
 * end-time suggestion when it found one.
 */
export type ClosableTrip = TripDetails & { suggestedEndAt?: string | null };

type Props = {
  fleetId: string;
  trip: ClosableTrip;
  /** Label of the secondary action - "Back" inside the fleet-wide list, "Cancel" on a details page. */
  cancelLabel?: string;
  onCancel: () => void;
  onClosed?: () => void;
};

/** How often the "current time" upper bound of the end-date picker is refreshed while open. */
const NOW_REFRESH_INTERVAL_MS = 30000;

export const getTripLabel = (trip: ClosableTrip): string => {
  const vehicleLabel = trip.vehicle?.licensePlateNumber || trip.deviceId || 'Unknown vehicle';
  const driverLabel = trip.driver ? `${trip.driver.firstName} ${trip.driver.lastName}` : 'No driver assigned';
  return `${vehicleLabel} - ${driverLabel}`;
};

/** Earliest end time accepted for a trip - one second after it started. */
export const getMinEndAt = (trip: ClosableTrip): Date | null => {
  const startedAt = Date.parse(trip.startAt);
  return Number.isFinite(startedAt) ? new Date(startedAt + 1000) : null;
};

const clampToAllowedRange = (date: Date, lowerBound: Date, upperBound: Date): Date => {
  if (date.getTime() < lowerBound.getTime()) return new Date(lowerBound);
  if (date.getTime() > upperBound.getTime()) return new Date(upperBound);
  return date;
};

/**
 * The start of the next trip on the same device/vehicle pair, when the backend found one and it
 * still falls inside the allowed window. Shown as a hint - the moment the trip must have been over
 * by - but the end time defaults to when the user asked to close the trip.
 */
export const getSuggestedEndAt = (trip: ClosableTrip, now: Date): Date | null => {
  if (!trip.suggestedEndAt) return null;
  const suggested = Date.parse(trip.suggestedEndAt);
  const minEndAt = getMinEndAt(trip);
  if (!Number.isFinite(suggested) || !minEndAt) return null;
  if (suggested < minEndAt.getTime() || suggested > now.getTime()) return null;
  return new Date(suggested);
};

/**
 * End date and time picker for closing a single ongoing trip. It is mounted when the user asks to
 * close a trip, so the moment of that click is the default end time.
 */
function CloseTripForm({
  fleetId,
  trip,
  cancelLabel = 'Back',
  onCancel,
  onClosed,
}: Props): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());
  const [endAt, setEndAt] = useState<Date | null>(now);
  const [isClosing, setClosing] = useState(false);

  const { data: fleetData } = useGetFleet(fleetId);
  const { mutateAsync: closeOngoingTrip } = useCloseOngoingTrip();

  // The upper bound is "now", so it has to keep moving while the form stays open.
  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), NOW_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, []);

  const minEndAt = getMinEndAt(trip);
  const suggestion = getSuggestedEndAt(trip, now);

  const handleEndAtChange = (date: Date | null): void => {
    if (!date || !minEndAt) {
      setEndAt(null);
      return;
    }
    // Clicking a day keeps the previously selected time, which can land outside the allowed
    // window - snap it back in.
    setEndAt(clampToAllowedRange(date, minEndAt, new Date()));
  };

  const handleUseNow = (): void => {
    const currentNow = new Date();
    setNow(currentNow);
    setEndAt(currentNow);
  };

  const isTimeAllowed = (time: Date): boolean => {
    if (!minEndAt) return false;
    const value = time.getTime();
    return value >= minEndAt.getTime() && value <= now.getTime();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!endAt || !minEndAt || isClosing) return;

    const closedAt = clampToAllowedRange(endAt, minEndAt, new Date()).toISOString();
    setClosing(true);
    try {
      await closeOngoingTrip({ fleetId, tripId: trip._id, endAt: closedAt });
      toast.success('Ongoing trip closed');
      onClosed?.();
    } catch {
      toast.error('Failed to close the ongoing trip');
    } finally {
      setClosing(false);
    }
  };

  const allowedRangeHint = minEndAt
    ? `Allowed range: ${getFormattedDateAndTime(minEndAt, true)}`
      + ` - ${getFormattedDateAndTime(now, true)} (${getTimeZone(now)})`
    : '';

  return (
    <S.Form>
      <div className="selected-trip">
        <span className="primary">{getTripLabel(trip)}</span>
        <span className="secondary">
          {`Started ${getFormattedDateAndTime(trip.startAt)}`}
          {` (${getTimeZone(trip.startAt)}) - ongoing for `}
          <span className="duration">{getTripDuration(trip.startAt, null)}</span>
        </span>
        {!!suggestion && (
          <span className="suggestion">
            {'Next trip on this vehicle started '}
            {getFormattedDateAndTime(suggestion, true)}
            {` (${getTimeZone(suggestion)})`}
          </span>
        )}
      </div>

      <div className="end-time">
        <span className="section-title">Trip end date and time</span>
        <span className="hint">{allowedRangeHint}</span>
        {!!minEndAt && (
          <>
            <ReactDatePicker
              inline
              selected={endAt}
              onChange={handleEndAtChange}
              minDate={minEndAt}
              maxDate={now}
              filterTime={isTimeAllowed}
              showTimeSelect
              timeIntervals={15}
              timeCaption="Time"
              timeFormat="HH:mm"
              calendarStartDay={fleetData?.settings?.weekStartDay ?? 1}
            />
            <div className="selected-row">
              <span className={`selected-value ${endAt ? '' : 'empty'}`}>
                {endAt
                  ? `The trip will be closed at ${getFormattedDateAndTime(endAt, true)}`
                    + ` (${getTimeZone(endAt)})`
                  : 'No end date and time selected yet.'}
              </span>
              <Button
                className="use-now"
                height="28px"
                padding="4px 16px"
                onClick={handleUseNow}
                disabled={isClosing}
              >
                <p>Now</p>
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="actions">
        <Button padding="7px 40px" onClick={onCancel} disabled={isClosing}>{cancelLabel}</Button>
        <Button
          padding="7px 40px"
          onClick={handleConfirm}
          disabled={!endAt || isClosing}
          background={theme.colors.main_cta}
          textColor={theme.colors.black}
          hoverBackground={theme.colors.white}
          hoverColor={theme.colors.black}
        >
          {isClosing ? 'Closing...' : 'Close trip'}
        </Button>
      </div>
    </S.Form>
  );
}

export default CloseTripForm;
